import { memo } from 'react';
import { Col, Divider, Row, Skeleton } from 'antd';
import { useWindowSize } from 'usehooks-ts';

import { Auction } from 'models/auction/Auction';

import AuctionImageGallery from 'pages/auctions/AuctionDetail/components/AuctionImageGallery';

import AuctionTitle from './components/AuctionTitle';
import AuctionPayment from './components/AuctionPayment';
import AuctionSchedule from './components/AuctionSchedule';
import AuctionShipping from './components/AuctionShipping';
import AuctionStatistics from './components/AuctionStatistics';
import AuctionDescription from './components/AuctionDescription';
import BiddingButton from 'pages/bids/CreateBid/BiddingButton';

import styles from './AuctionView.module.css';
import NotFound from 'pages/common/errors/NotFound';
import InvitationCode from './components/InvitationCode';

interface AuctionViewProps {
  auction: Auction | null;
  setAuction?: (auction: Auction) => void;
}

function AuctionView({ auction, setAuction }: AuctionViewProps) {
  const { width } = useWindowSize();
  const isDesktop = width >= 1080;
  const span = isDesktop ? 12 : undefined;

  if (['draft', 'deleted'].includes(auction?.state ?? '')) {
    return <NotFound />;
  }

  return (
    <Row
      className={`${styles.container}${isDesktop ? '' : ' topdown-flex'}`}
      gutter={isDesktop ? 48 : undefined}
      wrap={false}
    >
      <Col span={span}>
        {auction ? (
          <div className={isDesktop ? styles.sticky : ''}>
            <AuctionImageGallery
              imageUrls={auction.images.map((image) => image.url)}
              showFullScreenButton={isDesktop}
            />
          </div>
        ) : (
          isDesktop && <Skeleton.Image className={styles['img-skeleton']} active />
        )}
      </Col>
      <Col span={span}>
        {auction ? (
          <>
            <AuctionTitle auction={auction} />
            <InvitationCode auction={auction} />
            <AuctionStatistics auction={auction} />
            <Divider className={styles['main-divider']} />
            <AuctionSchedule auction={auction} />
            <Divider />
            <AuctionShipping auction={auction} />
            <Divider />
            <AuctionPayment auction={auction} />
            <Divider />
            <AuctionDescription description={auction.description} />
            {setAuction && (
              <div className={styles['bidding-btn']}>
                <BiddingButton auction={auction} setAuction={setAuction} showShadow />
              </div>
            )}
          </>
        ) : (
          <Skeleton active />
        )}
      </Col>
    </Row>
  );
}

export default memo(AuctionView);
